import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, 
  Settings, 
  TrendingUp, 
  TrendingDown,
  Users,
  Ticket,
  Euro,
  PieChart,
  Calendar,
  Building2,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  Mail,
  Send
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const AdminProfitLoss = () => {
  const [loading, setLoading] = useState(true);
  const [monuments, setMonuments] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [memberShares, setMemberShares] = useState([]);
  const [members, setMembers] = useState([]);
  
  // Selected monument for P&L
  const [selectedMonument, setSelectedMonument] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [plReport, setPLReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Dialogs
  const [investmentDialog, setInvestmentDialog] = useState({ open: false, monument: null });
  const [shareDialog, setShareDialog] = useState({ open: false, share: null });
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [investmentForm, setInvestmentForm] = useState({
    monument_id: '',
    share_price_eur: '',
    base_ticket_cost_eur: '',
    service_fee_eur: ''
  });
  const [shareForm, setShareForm] = useState({
    user_id: '',
    monument_id: '',
    shares: 1
  });

  // Expand daily breakdown
  const [showDailyBreakdown, setShowDailyBreakdown] = useState(false);
  
  // Send P&L Report state
  const [sendingReport, setSendingReport] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [monumentsRes, investmentsRes, sharesRes, usersRes] = await Promise.all([
        adminAPI.listMonuments(),
        adminAPI.listInvestments(),
        adminAPI.listMemberShares({}),
        adminAPI.listUsers({ role: 'user', user_type: 'member' })
      ]);
      
      setMonuments(monumentsRes.data);
      setInvestments(investmentsRes.data);
      setMemberShares(sharesRes.data);
      setMembers(usersRes.data.filter(u => u.user_type === 'member'));
      
      // Auto-select first monument with investment settings
      if (investmentsRes.data.length > 0 && !selectedMonument) {
        setSelectedMonument(investmentsRes.data[0].monument_id);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadPLReport = async () => {
    if (!selectedMonument || !selectedMonth) return;
    
    setLoadingReport(true);
    try {
      const res = await adminAPI.getPLReport(selectedMonument, selectedMonth);
      setPLReport(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load P&L report');
      setPLReport(null);
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (selectedMonument && selectedMonth) {
      loadPLReport();
    }
  }, [selectedMonument, selectedMonth]);

  const handleSaveInvestment = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data = {
        monument_id: investmentForm.monument_id,
        share_price_eur_cents: Math.round(parseFloat(investmentForm.share_price_eur) * 100),
        base_ticket_cost_eur_cents: Math.round(parseFloat(investmentForm.base_ticket_cost_eur) * 100),
        service_fee_eur_cents: Math.round(parseFloat(investmentForm.service_fee_eur) * 100)
      };
      
      if (investmentDialog.monument) {
        // Update existing
        await adminAPI.updateInvestment(investmentForm.monument_id, data);
        toast.success('Investment settings updated');
      } else {
        // Create new
        await adminAPI.createInvestment(data);
        toast.success('Investment settings created');
      }
      
      setInvestmentDialog({ open: false, monument: null });
      loadData();
      loadPLReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save investment settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveShare = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (shareDialog.share) {
        // Update existing
        await adminAPI.updateMemberShare(shareDialog.share.id, { shares: parseInt(shareForm.shares) });
        toast.success('Share updated');
      } else {
        // Create new
        await adminAPI.createMemberShare({
          user_id: shareForm.user_id,
          monument_id: shareForm.monument_id,
          shares: parseInt(shareForm.shares)
        });
        toast.success('Share assigned');
      }
      
      setShareDialog({ open: false, share: null });
      loadData();
      loadPLReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save share');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteShare = async (shareId) => {
    if (!confirm('Are you sure you want to remove this share?')) return;
    
    try {
      await adminAPI.deleteMemberShare(shareId);
      toast.success('Share removed');
      loadData();
      loadPLReport();
    } catch (error) {
      toast.error('Failed to remove share');
    }
  };

  const handleSendPLReport = async () => {
    if (!selectedMonument || !selectedMonth) {
      toast.error('Please select a monument and month first');
      return;
    }
    
    setSendingReport(true);
    try {
      const res = await adminAPI.sendPnlReport(selectedMonument, selectedMonth);
      if (res.data.sent_count > 0) {
        toast.success(`P&L report sent to ${res.data.sent_count} shareholder(s)`);
      } else {
        toast.info('No shareholders found to send the report to');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send P&L report');
    } finally {
      setSendingReport(false);
    }
  };

  const openInvestmentDialog = (monument = null) => {
    if (monument) {
      const inv = investments.find(i => i.monument_id === monument.id);
      setInvestmentForm({
        monument_id: monument.id,
        share_price_eur: inv ? (inv.share_price_eur_cents / 100).toString() : '',
        base_ticket_cost_eur: inv ? (inv.base_ticket_cost_eur_cents / 100).toString() : '',
        service_fee_eur: inv ? (inv.service_fee_eur_cents / 100).toString() : ''
      });
      setInvestmentDialog({ open: true, monument: inv });
    } else {
      setInvestmentForm({
        monument_id: '',
        share_price_eur: '',
        base_ticket_cost_eur: '',
        service_fee_eur: ''
      });
      setInvestmentDialog({ open: true, monument: null });
    }
  };

  const openShareDialog = (share = null) => {
    if (share) {
      setShareForm({
        user_id: share.user_id,
        monument_id: share.monument_id,
        shares: share.shares
      });
    } else {
      setShareForm({
        user_id: '',
        monument_id: selectedMonument || '',
        shares: 1
      });
    }
    setShareDialog({ open: true, share });
  };

  // Generate month options (6 months ahead + 12 months back)
  const monthOptions = [];
  const now = new Date();
  // Add 6 future months first
  for (let i = 6; i >= 1; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    monthOptions.push({ key, label });
  }
  // Add current and past 12 months
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    monthOptions.push({ key, label });
  }

  // Get monuments without investment settings
  const monumentsWithoutInvestment = monuments.filter(
    m => !investments.find(i => i.monument_id === m.id)
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Profit & Loss</h1>
            <p className="text-slate-500">Manage investments, shares, and view P&L reports</p>
          </div>
        </div>

        <Tabs defaultValue="report" className="space-y-6">
          <TabsList>
            <TabsTrigger value="report" data-testid="tab-report">
              <PieChart className="w-4 h-4 mr-2" />
              P&L Report
            </TabsTrigger>
            <TabsTrigger value="investments" data-testid="tab-investments">
              <Settings className="w-4 h-4 mr-2" />
              Investment Settings
            </TabsTrigger>
            <TabsTrigger value="shares" data-testid="tab-shares">
              <Users className="w-4 h-4 mr-2" />
              Member Shares
            </TabsTrigger>
          </TabsList>

          {/* P&L Report Tab */}
          <TabsContent value="report" className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label>Monument</Label>
                    <Select value={selectedMonument} onValueChange={setSelectedMonument}>
                      <SelectTrigger data-testid="select-monument">
                        <SelectValue placeholder="Select monument" />
                      </SelectTrigger>
                      <SelectContent>
                        {investments.map((inv) => (
                          <SelectItem key={inv.monument_id} value={inv.monument_id}>
                            {inv.monument_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label>Month</Label>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger data-testid="select-month">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {monthOptions.map((opt) => (
                          <SelectItem key={opt.key} value={opt.key}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={loadPLReport} 
                    disabled={loadingReport || !selectedMonument}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="load-report-btn"
                  >
                    {loadingReport ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PieChart className="w-4 h-4 mr-2" />}
                    Load Report
                  </Button>
                </div>
              </CardContent>
            </Card>

            {!selectedMonument && investments.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Settings className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">No Investment Settings</h3>
                  <p className="text-slate-500 mb-4">Configure investment settings for a monument first to generate P&L reports.</p>
                  <Button onClick={() => openInvestmentDialog()} className="bg-indigo-600 hover:bg-indigo-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Investment Settings
                  </Button>
                </CardContent>
              </Card>
            )}

            {plReport && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-blue-100 rounded-lg">
                          <Ticket className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Tickets Available</p>
                          <p className="text-2xl font-bold text-slate-900">{plReport.monthly_summary.tickets_available}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-green-100 rounded-lg">
                          <TrendingUp className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Tickets Sold</p>
                          <p className="text-2xl font-bold text-slate-900">
                            {plReport.monthly_summary.tickets_sold_member + plReport.monthly_summary.tickets_sold_external}
                          </p>
                          <p className="text-xs text-slate-400">
                            {plReport.monthly_summary.tickets_sold_member} members, {plReport.monthly_summary.tickets_sold_external} external
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-red-100 rounded-lg">
                          <TrendingDown className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Tickets Unsold</p>
                          <p className="text-2xl font-bold text-slate-900">{plReport.monthly_summary.tickets_unsold}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-lg ${plReport.monthly_summary.profit_loss_eur >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                          <Euro className={`w-6 h-6 ${plReport.monthly_summary.profit_loss_eur >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Net Profit/Loss</p>
                          <p className={`text-2xl font-bold ${plReport.monthly_summary.profit_loss_eur >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            €{plReport.monthly_summary.profit_loss_eur.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Financial Summary */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Financial Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Total Revenue</span>
                          <span className="font-semibold text-green-600">€{plReport.monthly_summary.total_revenue_eur.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Total Cost</span>
                          <span className="font-semibold text-red-600">-€{plReport.monthly_summary.total_cost_eur.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-300">
                          <span className="font-medium">Net Profit/Loss</span>
                          <span className={`font-bold ${plReport.monthly_summary.profit_loss_eur >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            €{plReport.monthly_summary.profit_loss_eur.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 bg-indigo-50 px-3 rounded-lg">
                          <span className="font-medium text-indigo-700">Profit per Share</span>
                          <span className={`font-bold ${plReport.monthly_summary.profit_per_share_eur >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            €{plReport.monthly_summary.profit_per_share_eur.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Investment Info</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Share Price</span>
                          <span className="font-medium">€{plReport.investment_settings.share_price_eur.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Base Ticket Cost</span>
                          <span className="font-medium">€{plReport.investment_settings.base_ticket_cost_eur}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Service Fee</span>
                          <span className="font-medium">€{plReport.investment_settings.service_fee_eur}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-slate-600">Total Cost per Ticket</span>
                          <span className="font-medium">€{plReport.investment_settings.total_cost_per_ticket_eur}</span>
                        </div>
                        <div className="flex justify-between py-2 bg-slate-50 px-3 rounded-lg">
                          <span className="font-medium">Total Investors / Shares</span>
                          <span className="font-bold text-indigo-600">
                            {plReport.shares_summary.total_investors} / {plReport.shares_summary.total_shares}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Member Breakdown */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Member P&L Breakdown</CardTitle>
                      <CardDescription>Profit/loss per member based on their shares</CardDescription>
                    </div>
                    {plReport.member_breakdown.length > 0 && (
                      <Button
                        onClick={handleSendPLReport}
                        disabled={sendingReport}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="send-pnl-report-btn"
                      >
                        {sendingReport ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Email P&L Report
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Member</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead className="text-center">Shares</TableHead>
                          <TableHead className="text-right">Profit/Loss</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {plReport.member_breakdown.map((member) => (
                          <TableRow key={member.user_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{member.user_name}</p>
                                <p className="text-sm text-slate-500">{member.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-500">{member.company_name || '-'}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">{member.shares}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={`font-semibold ${member.profit_loss_eur >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                €{member.profit_loss_eur.toLocaleString()}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                        {plReport.member_breakdown.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                              No member shares configured for this monument
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Daily Breakdown */}
                <Card>
                  <CardHeader 
                    className="cursor-pointer" 
                    onClick={() => setShowDailyBreakdown(!showDailyBreakdown)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Daily Breakdown</CardTitle>
                        <CardDescription>Click to {showDailyBreakdown ? 'hide' : 'show'} daily P&L details</CardDescription>
                      </div>
                      {showDailyBreakdown ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </CardHeader>
                  {showDailyBreakdown && (
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-center">Available</TableHead>
                            <TableHead className="text-center">Sold (M)</TableHead>
                            <TableHead className="text-center">Sold (E)</TableHead>
                            <TableHead className="text-center">Unsold</TableHead>
                            <TableHead className="text-right">Revenue</TableHead>
                            <TableHead className="text-right">Cost</TableHead>
                            <TableHead className="text-right">P&L</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {plReport.daily_breakdown.map((day) => (
                            <TableRow key={day.date}>
                              <TableCell className="font-medium">{day.date}</TableCell>
                              <TableCell className="text-center">{day.tickets_available}</TableCell>
                              <TableCell className="text-center text-green-600">{day.tickets_sold_member}</TableCell>
                              <TableCell className="text-center text-blue-600">{day.tickets_sold_external}</TableCell>
                              <TableCell className="text-center text-red-600">{day.tickets_unsold}</TableCell>
                              <TableCell className="text-right text-green-600">€{(day.total_revenue_cents / 100).toFixed(2)}</TableCell>
                              <TableCell className="text-right text-red-600">€{(day.total_cost_cents / 100).toFixed(2)}</TableCell>
                              <TableCell className={`text-right font-semibold ${day.profit_loss_cents >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                €{(day.profit_loss_cents / 100).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                          {plReport.daily_breakdown.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                                No ticket data for this month
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  )}
                </Card>
              </>
            )}
          </TabsContent>

          {/* Investment Settings Tab */}
          <TabsContent value="investments" className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-slate-600">Configure share price and ticket costs for each monument</p>
              {monumentsWithoutInvestment.length > 0 && (
                <Button onClick={() => openInvestmentDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-investment-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Investment Settings
                </Button>
              )}
            </div>

            <Card>
              <CardContent className="pt-6">
                {investments.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-700 mb-2">No Investment Settings</h3>
                    <p className="text-slate-500 mb-4">Add investment settings for monuments to track P&L</p>
                    <Button onClick={() => openInvestmentDialog()} className="bg-indigo-600 hover:bg-indigo-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Investment Settings
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Monument</TableHead>
                        <TableHead className="text-right">Share Price</TableHead>
                        <TableHead className="text-right">Ticket Cost</TableHead>
                        <TableHead className="text-right">Service Fee</TableHead>
                        <TableHead className="text-center">Investors</TableHead>
                        <TableHead className="text-center">Total Shares</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {investments.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell className="font-medium">{inv.monument_name}</TableCell>
                          <TableCell className="text-right">€{(inv.share_price_eur_cents / 100).toLocaleString()}</TableCell>
                          <TableCell className="text-right">€{(inv.base_ticket_cost_eur_cents / 100).toFixed(2)}</TableCell>
                          <TableCell className="text-right">€{(inv.service_fee_eur_cents / 100).toFixed(2)}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{inv.total_investors}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-indigo-100 text-indigo-700">{inv.total_shares}</Badge>
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openInvestmentDialog(monuments.find(m => m.id === inv.monument_id))}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Member Shares Tab */}
          <TabsContent value="shares" className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-slate-600">Manage member shares for each monument</p>
              <Button onClick={() => openShareDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-share-btn">
                <Plus className="w-4 h-4 mr-2" />
                Assign Share
              </Button>
            </div>

            <Card>
              <CardContent className="pt-6">
                {memberShares.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-700 mb-2">No Member Shares</h3>
                    <p className="text-slate-500 mb-4">Assign shares to members to track their profit/loss</p>
                    <Button onClick={() => openShareDialog()} className="bg-indigo-600 hover:bg-indigo-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Assign Share
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Monument</TableHead>
                        <TableHead className="text-center">Shares</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {memberShares.map((share) => (
                        <TableRow key={share.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{share.user_name}</p>
                              <p className="text-sm text-slate-500">{share.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-500">{share.company_name || '-'}</TableCell>
                          <TableCell>{share.monument_name}</TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-indigo-100 text-indigo-700">{share.shares}</Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openShareDialog(share)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteShare(share.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Investment Settings Dialog */}
      <Dialog open={investmentDialog.open} onOpenChange={(open) => setInvestmentDialog({ open, monument: open ? investmentDialog.monument : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{investmentDialog.monument ? 'Edit' : 'Add'} Investment Settings</DialogTitle>
            <DialogDescription>Configure share price and ticket costs for a monument</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveInvestment} className="space-y-4">
            {!investmentDialog.monument && (
              <div className="space-y-2">
                <Label>Monument</Label>
                <Select 
                  value={investmentForm.monument_id} 
                  onValueChange={(v) => setInvestmentForm({...investmentForm, monument_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select monument" />
                  </SelectTrigger>
                  <SelectContent>
                    {monumentsWithoutInvestment.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Share Price (€)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="1680.00"
                value={investmentForm.share_price_eur}
                onChange={(e) => setInvestmentForm({...investmentForm, share_price_eur: e.target.value})}
                required
                data-testid="share-price-input"
              />
              <p className="text-xs text-slate-500">Amount each member pays per share</p>
            </div>
            <div className="space-y-2">
              <Label>Base Ticket Cost (€)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="20.00"
                value={investmentForm.base_ticket_cost_eur}
                onChange={(e) => setInvestmentForm({...investmentForm, base_ticket_cost_eur: e.target.value})}
                required
                data-testid="ticket-cost-input"
              />
              <p className="text-xs text-slate-500">Cost to purchase each ticket</p>
            </div>
            <div className="space-y-2">
              <Label>Service Fee (€)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="2.00"
                value={investmentForm.service_fee_eur}
                onChange={(e) => setInvestmentForm({...investmentForm, service_fee_eur: e.target.value})}
                required
                data-testid="service-fee-input"
              />
              <p className="text-xs text-slate-500">Additional service fee per ticket</p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setInvestmentDialog({ open: false, monument: null })}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Member Share Dialog */}
      <Dialog open={shareDialog.open} onOpenChange={(open) => setShareDialog({ open, share: open ? shareDialog.share : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{shareDialog.share ? 'Edit' : 'Assign'} Member Share</DialogTitle>
            <DialogDescription>Assign shares to a member for a monument</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveShare} className="space-y-4">
            {!shareDialog.share && (
              <>
                <div className="space-y-2">
                  <Label>Member</Label>
                  <Select 
                    value={shareForm.user_id} 
                    onValueChange={(v) => setShareForm({...shareForm, user_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select member" />
                    </SelectTrigger>
                    <SelectContent>
                      {members.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name} {m.company_name ? `(${m.company_name})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Monument</Label>
                  <Select 
                    value={shareForm.monument_id} 
                    onValueChange={(v) => setShareForm({...shareForm, monument_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select monument" />
                    </SelectTrigger>
                    <SelectContent>
                      {investments.map((inv) => (
                        <SelectItem key={inv.monument_id} value={inv.monument_id}>
                          {inv.monument_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Number of Shares</Label>
              <Input
                type="number"
                min="1"
                placeholder="1"
                value={shareForm.shares}
                onChange={(e) => setShareForm({...shareForm, shares: parseInt(e.target.value) || 1})}
                required
                data-testid="shares-input"
              />
              <p className="text-xs text-slate-500">How many shares this member owns</p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShareDialog({ open: false, share: null })}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminProfitLoss;
